import React, { FC, useMemo } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { getEnvironment } from '@minecraft.environment';
import { useUserContext } from '@blocs.auth';
import { captureExceptionEvent } from '@blocs.observability';
import { GOOGLE_MAPS_API_KEY } from './constants';

// matches an unsubstituted deploy placeholder (e.g. locally/in tests, before envsubst has run against a built bundle)
const UNRESOLVED_TEMPLATE = /^\$\{.+\}$/;

export type PlacesApiProviderProps = {
  /** ISO 3166-1 alpha-2 region code used to bias Places results (usually the account/billing country) */
  region?: string;
  children: React.ReactNode;
};

/**
 * Loads the Google Maps JS SDK's "places" library and makes it available to
 * any `<AddressSuggestion />` rendered underneath it. Mount this once around
 * a form section, the same way `<RecurlyProvider>` wraps `<PaymentMethodsManager />`
 * in AddCardModal.tsx.
 *
 * If `GOOGLE_MAPS_API_KEY` isn't configured (e.g. locally before a dev key is
 * set, or the env var hasn't been provisioned for an environment yet), this
 * renders children unwrapped and every `<AddressSuggestion />` underneath
 * falls back to a plain text input with no suggestions.
 *
 * @example
 * <PlacesApiProvider region={billingCountryCode}>
 *   <AddressSuggestion
 *     id="address1"
 *     value={values.address1}
 *     onChange={(value) => setFieldValue('address1', value)}
 *     onSelectAddress={(address) => setFieldValue('address1', address.address1)}
 *   />
 * </PlacesApiProvider>
 */
export const PlacesApiProvider: FC<PlacesApiProviderProps> = ({ region, children }) => {
  const apiKey = getEnvironment()?.[GOOGLE_MAPS_API_KEY];
  const { myAccount } = useUserContext();

  const regionToUse = useMemo(() => {
    const normalizedRegion = region?.trim().toUpperCase();
    return normalizedRegion || myAccount?.country?.code;
  }, [region, myAccount?.country?.code]);

  if (!apiKey || UNRESOLVED_TEMPLATE.test(apiKey)) {
    return <>{children}</>;
  }

  return (
    <APIProvider
      apiKey={apiKey}
      libraries={['places']}
      region={regionToUse}
      onError={(error) => {
        captureExceptionEvent(error instanceof Error ? error : new Error(String(error)), {
          location: 'PlacesApiProvider',
        });
      }}
    >
      {children}
    </APIProvider>
  );
};
